import { useEffect, useRef, useState } from 'react'
import { Empty, Spin } from 'antd'
import { adminApi } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import {
  fetchAttachmentBlob,
  fetchAttachmentPreviewDocx,
  listPreviewableFiles,
  pickPrimaryPreviewFileIndex,
  renderDocxHtml,
  resolvePreviewKind,
  type FileRec,
  type PreviewKind,
} from './knowledgeFilePreview'
import { DocQuoteHighlightViewer } from './DocQuoteHighlightViewer'
import { PdfQuoteHighlightViewer } from './PdfQuoteHighlightViewer'

type ScopeParams = { scope: KnowledgeScope }

type ItemMeta = {
  id: number
  title: string
  files_json?: FileRec[] | null
}

export type BidParseSourceViewerProps = {
  itemId: number
  scopeParams: ScopeParams
  active?: boolean
  pageNo?: number | null
  quote?: string | null
  onPreviewKind?: (kind: PreviewKind | null) => void
  minHeight?: string
}

const LOCATE_KINDS = new Set<PreviewKind>(['pdf', 'docx', 'doc'])

export function BidParseSourceViewer({
  itemId,
  scopeParams,
  active = true,
  pageNo = null,
  quote = null,
  onPreviewKind,
  minHeight = 'calc(100vh - 400px)',
}: BidParseSourceViewerProps) {
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [previewKind, setPreviewKind] = useState<PreviewKind | null>(null)
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const [docxHtml, setDocxHtml] = useState('')
  const blobRef = useRef<string | null>(null)

  useEffect(() => {
    return () => {
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current)
        blobRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!active || !itemId) {
      setPreviewKind(null)
      setPdfBlobUrl(null)
      setDocxHtml('')
      setLoadError(null)
      onPreviewKind?.(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setLoadError(null)
    setPdfBlobUrl(null)
    setDocxHtml('')

    const replaceBlobUrl = (next: string | null) => {
      if (blobRef.current && blobRef.current !== next) {
        URL.revokeObjectURL(blobRef.current)
      }
      blobRef.current = next
      setPdfBlobUrl(next)
    }

    adminApi
      .get<ItemMeta>(`/admin/knowledge/items/${itemId}`, { params: scopeParams })
      .then(async (r) => {
        if (cancelled) return
        const files = listPreviewableFiles(r.data.files_json)
        if (!files.length) {
          setLoadError('该条目无上传文件')
          setPreviewKind(null)
          onPreviewKind?.(null)
          return
        }
        const file = files[pickPrimaryPreviewFileIndex(files)]!
        const kind = resolvePreviewKind(file)
        setPreviewKind(kind)
        onPreviewKind?.(kind)

        if (!LOCATE_KINDS.has(kind)) {
          setLoadError('当前主附件格式暂不支持原文标红定位（支持 PDF、Word）')
          return
        }

        if (kind === 'pdf') {
          const { url } = await fetchAttachmentBlob(itemId, file, scopeParams)
          if (cancelled) {
            URL.revokeObjectURL(url)
            return
          }
          replaceBlobUrl(url)
          return
        }

        const blob =
          kind === 'docx'
            ? (await fetchAttachmentBlob(itemId, file, scopeParams)).blob
            : await fetchAttachmentPreviewDocx(itemId, file, scopeParams)
        if (cancelled) return
        setDocxHtml(await renderDocxHtml(blob))
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : '加载文件失败')
          setPreviewKind(null)
          onPreviewKind?.(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [active, itemId, scopeParams, onPreviewKind])

  if (loading) {
    return (
      <div className="pdf-quote-viewer-loading" style={{ minHeight }}>
        <Spin tip="加载原文…" />
      </div>
    )
  }

  if (loadError) {
    return <Empty description={loadError} />
  }

  if (previewKind === 'pdf') {
    return (
      <PdfQuoteHighlightViewer
        blobUrl={pdfBlobUrl}
        pageNo={pageNo}
        quote={quote}
        minHeight={minHeight}
      />
    )
  }

  if (previewKind === 'docx' || previewKind === 'doc') {
    return <DocQuoteHighlightViewer html={docxHtml} quote={quote} minHeight={minHeight} />
  }

  return <Empty description="无法预览原文" />
}

/** @deprecated 使用 BidParseSourceViewer */
export const BidParsePdfViewer = BidParseSourceViewer
